import { useCallback, useState } from 'react';
import type { ProjectTask } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { toggleOpgelijst } from '@/api/tasks';
import type { CadJsonPayload } from '@/api/cadJson';

/** The CAD flow target — the fase a CAD import is being prepared for. */
export interface CadTarget {
  taskId: number;
  taskName: string;
}

export interface MateriaalCadState {
  /** The fase the CAD flow is open for, or null when closed. */
  target: CadTarget | null;
  /** The parsed payload once the user picked a JSON, or null (picker stage). */
  payload: CadJsonPayload | null;
  /** Open the CAD picker for a fase (resets any in-flight payload). */
  openCad: (fase: CadTarget) => void;
  /** Store the payload picked in the CadJsonPicker (advances to the preview). */
  setPayload: (payload: CadJsonPayload | null) => void;
  /** Close the whole CAD flow (clears target + payload). */
  close: () => void;
}

export interface MateriaalFaseControls {
  /** Local optimistic overrides keyed by taskId. */
  opgelijstOverrides: Record<number, boolean>;
  /** Resolve the effective opgelijst value for a task (override or stored). */
  getOpgelijstValue: (taskId: number) => boolean;
  /** Optimistically toggle opgelijst, reverting the override on API error. */
  onToggleOpgelijst: (taskId: number, value: boolean) => Promise<void>;
  cad: MateriaalCadState;
  /** Upload a CSV materiaallijst for a fase (delegates to the store). */
  onUpload: (faseId: number, file: File) => Promise<void>;
  /** Delete a materiaallijst picking (delegates to the store). */
  onDelete: (pickingId: number) => Promise<void>;
  /** Surface an error message to the shared uploadError store slice. */
  onError: (msg: string) => void;
  /** True while a materiaallijst upload is in flight. */
  isUploading: boolean;
}

/**
 * Shared orchestration for the per-fase materiaal controls (opgelijst toggle,
 * CAD import flow, CSV upload, picking delete). Extracted verbatim from
 * MateriaalTab so the dashboard section reuses the exact same glue instead of
 * duplicating it — the whole point being to eventually retire MateriaalTab.
 *
 * Behaviour-preserving: the optimistic toggle + revert, the upload/delete
 * store calls, and the CAD target/payload state machine all match the original
 * MateriaalTab implementation.
 *
 * `projectTasks` is optional and only used by `getOpgelijstValue` to read the
 * task's stored `x_studio_opgelijst` when there is no local override (matching
 * MateriaalTab's `projectTasks.find(...)`). Consumers that don't need the
 * stored fallback may omit it.
 */
export function useMateriaalFaseControls(
  projectId: number,
  projectTasks: ProjectTask[] = [],
): MateriaalFaseControls {
  const uploadMateriaallijst = useProjectViewStore((s) => s.uploadMateriaallijst);
  const deleteMateriaallijst = useProjectViewStore((s) => s.deleteMateriaallijst);
  const isUploading = useProjectViewStore((s) => s.uploadingMateriaal);

  const [opgelijstOverrides, setOpgelijstOverrides] = useState<Record<number, boolean>>({});
  const [cadTarget, setCadTarget] = useState<CadTarget | null>(null);
  const [cadPayload, setCadPayload] = useState<CadJsonPayload | null>(null);

  const getOpgelijstValue = useCallback(
    (taskId: number): boolean => {
      if (taskId in opgelijstOverrides) return opgelijstOverrides[taskId];
      const task = projectTasks.find((t) => t.id === taskId);
      return !!task?.x_studio_opgelijst;
    },
    [opgelijstOverrides, projectTasks],
  );

  const onToggleOpgelijst = useCallback(async (taskId: number, value: boolean) => {
    setOpgelijstOverrides((prev) => ({ ...prev, [taskId]: value }));
    try {
      await toggleOpgelijst(taskId, value);
    } catch {
      // Revert on error
      setOpgelijstOverrides((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    }
  }, []);

  const openCad = useCallback((fase: CadTarget) => {
    setCadTarget(fase);
    setCadPayload(null);
  }, []);

  const closeCad = useCallback(() => {
    setCadTarget(null);
    setCadPayload(null);
  }, []);

  const onUpload = useCallback(
    async (faseId: number, file: File) => {
      await uploadMateriaallijst(projectId, faseId, file);
    },
    [uploadMateriaallijst, projectId],
  );

  const onDelete = useCallback(
    async (pickingId: number) => {
      await deleteMateriaallijst(projectId, pickingId);
    },
    [deleteMateriaallijst, projectId],
  );

  const onError = useCallback((msg: string) => {
    useProjectViewStore.setState({ uploadError: msg });
  }, []);

  return {
    opgelijstOverrides,
    getOpgelijstValue,
    onToggleOpgelijst,
    cad: {
      target: cadTarget,
      payload: cadPayload,
      openCad,
      setPayload: setCadPayload,
      close: closeCad,
    },
    onUpload,
    onDelete,
    onError,
    isUploading,
  };
}
